// API utility functions with Bearer token authentication

import { getApiBaseUrl } from "@shared/config/apiBaseUrl";

export const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

function resolveBaseUrl() {
  const apigBase = getApiBaseUrl();
  if (apigBase) return apigBase;
  const currentHost = window.location.hostname;
  const port = window.location.port ? `:${window.location.port}` : '';
  const protocol = window.location.protocol;
  let apiHost = currentHost;
  if (currentHost.includes('.')) {
    const parts = currentHost.split('.');
    if (parts.length > 1) {
      if (currentHost.includes('.localhost')) {
        apiHost = 'localhost';
      } else {
        apiHost = parts.slice(1).join('.');
      }
    }
  }
  return `${protocol}//${apiHost}${port}`;
}

/** Full URL for an API path; respects REACT_APP_API_BASE_URL (e.g. API Gateway) or same-origin. */
export function apiUrl(path) {
  const base = resolveBaseUrl();
  if (path.startsWith("http")) return path;
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base.replace(/\/$/, "")}${p}`;
}

export const apiRequest = async (endpoint, options = {}) => {
  const baseUrl = resolveBaseUrl();
  const url = endpoint.startsWith("http") ? endpoint : `${baseUrl}${endpoint}`;

  const config = {
    headers: getAuthHeaders(),
    credentials: 'include',
    ...options,
  };

  if (options.headers) {
    config.headers = { ...config.headers, ...options.headers };
  }

  const response = await fetch(url, config);

  if (response.status === 401) {
    try {
      const loginUrl = `${baseUrl}/api/v1/auth/login`;
      const tokenResponse = await fetch(loginUrl, {
        credentials: 'include'
      });

      if (tokenResponse.ok) {
        const tokenData = await tokenResponse.json();
        localStorage.setItem('auth_token', tokenData.token);
        localStorage.setItem('token_expires', Date.now() + (tokenData.expires_in * 1000));

        const retryConfig = {
          ...config,
          headers: {
            ...config.headers,
            'Authorization': `Bearer ${tokenData.token}`
          }
        };
        return await fetch(url, retryConfig);
      }
    } catch {
      // Failed to refresh token
    }

    localStorage.removeItem('auth_token');
    localStorage.removeItem('token_expires');

    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const logoutPath = pathname.startsWith('/admin') ? '/admin/logout' : '/logout';
    window.location.replace(`${window.location.origin}${logoutPath}`);
    throw new Error('Session expired');
  }

  return response;
};

export const get = (endpoint) => apiRequest(endpoint);
export const post = (endpoint, data) => apiRequest(endpoint, {
  method: 'POST',
  body: JSON.stringify(data),
});
export const patch = (endpoint, data) => apiRequest(endpoint, {
  method: 'PATCH',
  body: JSON.stringify(data),
});
export const del = (endpoint) => apiRequest(endpoint, {
  method: 'DELETE',
});
